import React, { Component } from "react";
import _ from "underscore";
import PropTypes from "prop-types";
import { compose } from "redux";
import { connect } from "react-redux";

import AuthActions from "actions/auth-actions";
import { setPostAuthenticationClientSideAction } from "actions/post-authentication-actions";
import ReservationValidator from "helpers/reservation-validator";
import ReservationFormFields from "components/source/reservations/reservation-form-fields";
import { closeGridProductDrawer } from "../../../slices/gridProductDrawer-slice";
import {
  browserPropType,
  membershipStatePropType,
  ordersPropType,
  PDPContentPropType,
  productPropType,
  reservationPropType,
  userDataPropType,
} from "components/propTypes";
import { checkout, COOKIES } from "rtr-constants";
import { HEAP_AUTH_TRIGGER_TYPES } from "helpers/heap-helpers";
import { withFireWithUserData } from "components/source/hoc/with-fire-with-user-data";
import { getUserId, hasUserData } from "../hoc/with-user-data";
import CEBagActions from "actions/ce-bag-actions";
import ReservationsActions from "actions/reservations-actions";
import AtomErrorNotification from "components/source/atoms/atom-error-notification";
import { isBulk, isClearance, isRental } from "helpers/price-helper";
import { getDefaultShippingZip, getUserLocationZip } from "helpers/dfc-ux-helper";
import Cookies from "universal-cookie";

export class ReservationFormComponent extends Component {
  static propTypes = {
    action: PropTypes.string,
    activeOrders: ordersPropType,
    analyticsLoggingContext: PropTypes.object,
    browser: browserPropType,
    cmsContent: PDPContentPropType,
    deliveryCutoffs: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    displayModal: PropTypes.func.isRequired,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errors: PropTypes.object,
    fireWithUserData: PropTypes.func,
    gridProductDrawer: PropTypes.object,
    handleWayfindCtaClick: PropTypes.func,
    isBuyNow: PropTypes.bool,
    isHomepageDrawer: PropTypes.bool,
    isMobileViewport: PropTypes.bool,
    isOneSize: PropTypes.bool.isRequired,
    isRentable: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    logAddToBag: PropTypes.func,
    logOpenAvailability: PropTypes.func,
    membershipBag: PropTypes.object,
    membershipState: membershipStatePropType,
    narrow: PropTypes.bool,
    offerBackupSize: PropTypes.bool,
    onAddToBagFailure: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    // onChangeReservationInput is used as the onChange func
    // for new animated input fields. The function takes
    // four arguments - value, isValid, name, inputObj
    onChangeReservationInput: PropTypes.func,
    predictedZip: PropTypes.string,
    product: productPropType,
    recommendations: PropTypes.arrayOf(PropTypes.shape),
    registerClientSideError: PropTypes.func.isRequired,
    remaining: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reservation: reservationPropType,
    selectedSku: PropTypes.shape({
      size: PropTypes.string,
    }),
    showAuthModal: PropTypes.func.isRequired,
    submittingReservationCallback: PropTypes.func.isRequired,
    canRenderSplitTheBusiness: PropTypes.bool,
    updateReservation: PropTypes.func.isRequired,
    userData: userDataPropType,
    workingFilters: PropTypes.object,
    isSelectedSizeAvailable: PropTypes.func,
  };

  getAnalyticsLoggingContextHiddenFields() {
    const { analyticsLoggingContext, membershipState } = this.props;
    if (!analyticsLoggingContext) {
      return [];
    }

    // NW [EXPLANATION] 3/20/20: for non-subscription add-to-cart,
    // the `rezo_add_to_cart` pixel will be logged server-side.
    // if the user navigated to this form from a curated carousel,
    // analytics wants to know details about which carousel they came from.
    // those details will be passed to the server to be logged with the add_to_cart pixel.
    const carouselContextHiddenFields = [
      {
        name: "carouselContext[carousel_id]",
        value: analyticsLoggingContext.carousel_id,
      },
      {
        name: "carouselContext[nth_carousel]",
        value: analyticsLoggingContext.nth_carousel,
      },
      {
        name: "carouselContext[pick_mode]",
        value: analyticsLoggingContext.pick_mode,
      },
    ];

    // NW [EXPLANATION] 3/20/20: nth_style may not be populated if the user
    // navigated from a carousel --> non-curated grid --> pdp.
    const nthStyleIsUndefined = _.isUndefined(analyticsLoggingContext.nth_style);
    let nthStyleHiddenField;
    if (nthStyleIsUndefined) {
      nthStyleHiddenField = null;
    } else {
      nthStyleHiddenField = {
        name: "carouselContext[nth_style]",
        value: analyticsLoggingContext.nth_style,
      };
    }

    const membershipTierName = membershipState?.membershipTierName ?? false;
    let membershipTypeField;
    if (membershipTierName) {
      membershipTypeField = {
        name: "carouselContext[membership_type]",
        value: membershipTierName.toUpperCase(),
      };
    } else {
      membershipTypeField = null;
    }

    return [...carouselContextHiddenFields, nthStyleHiddenField, membershipTypeField];
  }

  getHiddenFields() {
    const { reservation } = this.props;

    if (!reservation) return [];

    const hiddenFields = [
      {
        name: "reservation[id]",
        value: reservation.id,
      },
      {
        name: "reservation[primaryHoldId]",
        value: reservation.primaryHoldId,
      },
      {
        name: "reservation[backupHoldId]",
        value: reservation.backupHoldId,
      },
    ].filter(({ value }) => value);

    if (hiddenFields.length > 0) {
      hiddenFields.push({
        name: "_method",
        value: "put",
      });
    }

    return hiddenFields;
  }

  onAuthCallback(isValid) {
    if (isValid) {
      /**
       * if single group checkout is enabled, add the item to CE Bag. we need to wrap this in
       * fireWithUserData, otherwise the ATB will try to fire before userdata is available,
       * causing issues
       */
      this.props.fireWithUserData(() => {
        this.addToCEBag();

        // still allow metrics etc to submit
        this.submitForm(false);
      });
    }
  }

  onSubmit = event => {
    const formValidationState = ReservationValidator.validate(
      this.props.reservation,
      this.props.product.skus,
      this.props.isRentable
    );

    // Update errors object whether valid or invalid
    this.props.registerClientSideError(formValidationState);

    const userId = getUserId(this.props.userData);

    if (userId) {
      if (formValidationState.isValid) {
        event.preventDefault();
        this.addToCEBag();

        // still allow metrics etc to submit
        this.submitForm(false);
        return;
      } else {
        // Reservation is invalid, do not submit form
        event.preventDefault();
        this.scrollToErrorInput(formValidationState);

        return;
      }
    } else {
      // User is not logged in
      event.preventDefault();

      if (!hasUserData(this.props.userData)) {
        const cookies = new Cookies();
        const isCasV1Enabled = cookies.get(COOKIES.CAS_V1_ENABLED) === "true";
        if (isCasV1Enabled) {
          const { isRentable, product, reservation } = this.props;

          const item = this.getItemForCEAddToBag();
          setPostAuthenticationClientSideAction(CEBagActions.addPostAuthentication.name, 10, {
            item,
            analyticsData: {
              productId: product?.id,
              retailPriceInteger: product?.retailPriceInteger,
            },
            reservation,
            isRentable,
          });
        }

        this.props.showAuthModal({
          callback: () => {
            this.onAuthCallback(formValidationState.isValid);
          },
          triggeredBy: HEAP_AUTH_TRIGGER_TYPES.CLICK_RESERVE_ATB,
        });
      }
    }
  };

  getItemForCEAddToBag = () => {
    const { product, reservation, predictedZip, userData } = this.props;

    const item = {};

    if (isRental(product?.price)) {
      item.zipCode = reservation.zipCode;
      item.type = checkout.orderItemTypes.RESERVATION;
      item.duration = reservation.duration;
      item.beginDate = reservation.date;
      item.endDate = reservation.returnDate;
      item.sku = reservation.primarySku.id;

      if (reservation.backupSku) {
        item.backupSku = reservation.backupSku.id;
      }
    } else if (isClearance(product?.price)) {
      item.zipCode = getUserLocationZip(getDefaultShippingZip(), userData.userProfile?.zip, predictedZip);
      item.sku = reservation.primarySku.id;
      item.type = checkout.orderItemTypes.CLEARANCE;
    } else if (isBulk(product?.price)) {
      item.zipCode = getUserLocationZip(getDefaultShippingZip(), userData.userProfile?.zip, predictedZip);
      item.sku = product.skus[0].id;
      item.type = checkout.orderItemTypes.BULK;
    }

    return item;
  };

  addToCEBag = () => {
    const { dispatch, product, gridProductDrawer } = this.props;

    const item = this.getItemForCEAddToBag();

    this.props.dispatch(
      CEBagActions.add(
        item,
        false,
        { productId: product?.id, retailPriceInteger: product?.retailPriceInteger },
        () => {
          // on ATB success
          dispatch(ReservationsActions.cancelReservationSubmission());
          if (gridProductDrawer) {
            dispatch(closeGridProductDrawer());
          }
        },
        error => {
          // on ATB error
          dispatch(ReservationsActions.cancelReservationSubmission());
          dispatch(ReservationsActions.setBaseError(error));
        }
      )
    );
  };

  FormInputs = {
    duration: ".reservation-date-window__label",
    zipCode: "label[for='destination-zip']",
    primarySku: "label[for='product-primary-size']",
    backupSku: "label[for='product-backup-size']']",
    date: ".datepicker-title, label[for='hold-date']",
  };

  scrollToErrorInput(formValidationState) {
    if (!this.props.isMobileViewport) {
      return;
    }
    const firstErrorInput = _.find(["duration", "zipCode", "primarySku", "backupSku", "date"], function (el) {
      return Object.prototype.hasOwnProperty.call(formValidationState, el);
    });

    // We need more padding at the top to accommodate for the input fields before scrolling
    const boxTop = document.querySelector(this.FormInputs[firstErrorInput])?.getBoundingClientRect?.().top;
    const docElemTop = document.documentElement.clientTop;
    const offsetTop = boxTop + window.scrollY - docElemTop;
    const reservationFieldPosition = offsetTop - 25;

    window.scrollTo({
      top: reservationFieldPosition,
      behavior: "smooth",
    });
  }

  submitForm(triggerSubmit) {
    const { isRentable, reservation } = this.props;
    this.props.logAddToBag(reservation, isRentable, true);

    // We need to trigger the form submit event if the user has logged in
    // after attempting to reserve an item. If a user is logged in and the form
    // is valid we do not need to trigger a submit, the form will submit on its own.
    if (triggerSubmit) {
      this.$form.submit();
    }

    this.props.submittingReservationCallback();
  }

  render() {
    let baseError;
    const { onAddToBagFailure } = this.props;

    if (this.props.errors?.base) {
      baseError = <AtomErrorNotification errorCopy={this.props.errors.base} />;
    }

    return (
      <form
        acceptCharset="UTF-8"
        action={this.props.action}
        className="reservation-form"
        method="post"
        onSubmit={this.onSubmit}
        data-test-id="reservation-form-product"
        ref={ref => {
          this.$form = ref;
        }}>
        {baseError}
        <ReservationFormFields
          analyticsLoggingContext={this.props.analyticsLoggingContext}
          browser={this.props.browser}
          cmsContent={this.props.cmsContent}
          deliveryCutoffs={this.props.deliveryCutoffs}
          dispatch={this.props.dispatch}
          displayModal={this.props.displayModal}
          displayedModal={this.props.displayedModal}
          errors={this.props.errors}
          gridProductDrawer={this.props.gridProductDrawer}
          handleWayfindCtaClick={this.props.handleWayfindCtaClick}
          isBuyNow={this.props.isBuyNow}
          isHomepageDrawer={this.props.isHomepageDrawer}
          isOneSize={this.props.isOneSize}
          isRentable={this.props.isRentable}
          isSubmitting={this.props.isSubmitting}
          logOpenAvailability={this.props.logOpenAvailability}
          membershipBag={this.props.membershipBag}
          membershipState={this.props.membershipState}
          narrow={this.props.narrow}
          offerBackupSize={this.props.offerBackupSize}
          onChange={this.props.onChange}
          onChangeReservationInput={this.props.onChangeReservationInput}
          onAddToBagFailure={onAddToBagFailure}
          product={this.props.product}
          recommendations={this.props.recommendations}
          remaining={this.props.remaining}
          reservation={this.props.reservation}
          selectedSku={this.props.selectedSku}
          canRenderSplitTheBusiness={this.props.canRenderSplitTheBusiness}
          updateReservation={this.props.updateReservation}
          userData={this.props.userData}
          workingFilters={this.props.workingFilters}
          isSelectedSizeAvailable={this.props.isSelectedSizeAvailable}
        />
        {_.map(this.getHiddenFields(), function (field, index) {
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <input key={"hf" + index} type="hidden" {...field} />;
        })}
        {this.getAnalyticsLoggingContextHiddenFields().map((field, index) => {
          if (field) {
            // eslint-disable-next-line react/jsx-props-no-spreading
            return <input key={"logging-hf" + index} type="hidden" {...field} />;
          }
        })}
      </form>
    );
  }
}

const mapStateToProps = ({ action, predictedZip, gridProductDrawer }) => ({ action, predictedZip, gridProductDrawer });

const mapDispatchToProps = dispatch => ({
  showAuthModal: options => dispatch(AuthActions.showAuthModal(options)),
  logAddToBag: (reservation, isRentable) => dispatch(CEBagActions.logAdd(reservation, isRentable)),
});

export default compose(withFireWithUserData, connect(mapStateToProps, mapDispatchToProps))(ReservationFormComponent);
